import React, { useState, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { FileText, Sparkles, Edit3, Check, Plus, Trash2 } from 'lucide-react';

const C5_GeneratePropositions = () => {
  const { dataC, updateDataRaw, nextStep, previousStep } = useWizard();
  const [propositions, setPropositions] = useState(dataC?.propositions || []);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    if (dataC?.propositions) {
      setPropositions(dataC.propositions);
    }
  }, [dataC?.propositions]);

  const generatePropositionsFromDiagram = () => {
    const nodes = dataC?.diagramNodes || [];
    const connections = dataC?.diagramConnections || [];
    
    if (connections.length === 0 || nodes.length < 2) {
      return;
    }
    
    const generated = connections.map((conn, index) => {
      const fromNode = nodes.find(n => n.id === conn.from);
      const toNode = nodes.find(n => n.id === conn.to);
      
      if (!fromNode || !toNode || !fromNode.name || !toNode.name) return null;

      let proposition = '';
      const relationWord = conn.type === 'direct' ? 'positively' : 
                          conn.type === 'inverse' ? 'negatively' : '';
      
      if (fromNode.type === 'mediator' || toNode.type === 'mediator') {
        const mediator = fromNode.type === 'mediator' ? fromNode : toNode;
        const other = fromNode.type === 'mediator' ? toNode : fromNode;
        proposition = `P${index + 1}: ${mediator.name || 'Variable'} mediates the relationship involving ${other.name || 'Variable'}.`;
      } else if (fromNode.type === 'moderator' || toNode.type === 'moderator') {
        const moderator = fromNode.type === 'moderator' ? fromNode : toNode;
        const other = fromNode.type === 'moderator' ? toNode : fromNode;
        proposition = `P${index + 1}: ${moderator.name || 'Variable'} moderates the effect on ${other.name || 'Variable'}.`;
      } else {
        proposition = `P${index + 1}: ${fromNode.name || 'Variable'} ${relationWord} influences ${toNode.name || 'Variable'}.`;
      }

      return {
        id: `prop_${conn.id}_${Date.now()}`,
        text: proposition,
        connectionId: conn.id,
        auto: true
      };
    }).filter(Boolean);

    const customProps = propositions.filter(p => !p.auto);
    const updated = [...generated, ...customProps];
    setPropositions(updated);
    updateDataRaw({ 'wizardC.propositions': updated });
  };

  const addCustomProposition = () => {
    const newProp = {
      id: Date.now(),
      text: `P${propositions.length + 1}: [Enter your proposition here]`,
      auto: false
    };
    const updated = [...propositions, newProp];
    setPropositions(updated);
    updateDataRaw({ 'wizardC.propositions': updated });
    setEditingId(newProp.id);
    setEditText(newProp.text);
  };

  const startEditing = (prop) => {
    setEditingId(prop.id);
    setEditText(prop.text);
  };

  const saveEdit = (id) => {
    const updated = propositions.map(p => 
      p.id === id ? { ...p, text: editText, auto: false } : p
    );
    setPropositions(updated);
    updateDataRaw({ 'wizardC.propositions': updated });
    setEditingId(null);
    setEditText('');
  };

  const deleteProposition = (id) => {
    const updated = propositions.filter(p => p.id !== id);
    setPropositions(updated);
    updateDataRaw({ 'wizardC.propositions': updated });
  };

  const handleContinue = () => {
    updateDataRaw({ 'wizardC.propositions': propositions });
    nextStep();
  };

  const connectionsCount = dataC?.diagramConnections?.length || 0;

  return (
    <div className="wizard-step">
      <div className="step-header">
        <div className="step-icon propositions-icon">
          <FileText size={32} />
        </div>
        <div>
          <h2>Step 7: Generate Propositions</h2>
          <p className="step-description">Create testable statements based on your framework diagram</p>
        </div>
      </div>

      <div className="step-content">
        <div className="generation-actions">
          <button 
            className="generate-btn"
            onClick={generatePropositionsFromDiagram}
            disabled={connectionsCount === 0}
          >
            <Sparkles size={18} />
            Auto-Generate from Diagram
          </button>
          <button className="add-btn" onClick={addCustomProposition}>
            <Plus size={18} />
            Add Custom Proposition
          </button>
        </div>

        {connectionsCount === 0 && (
          <div className="warning-box">
            No connections found in your diagram. Please go back to Step 3 and create relationships between constructs.
          </div>
        )}

        <div className="propositions-list">
          {propositions.length === 0 ? (
            <div className="empty-state">
              <FileText size={48} />
              <p>No propositions yet. Click "Auto-Generate" or "Add Custom" to create propositions.</p>
            </div>
          ) : (
            propositions.map((prop, index) => (
              <div key={prop.id} className={`proposition-item ${prop.auto ? 'auto-generated' : 'custom'}`}>
                {editingId === prop.id ? (
                  <div className="proposition-edit">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={2}
                      autoFocus
                    />
                    <div className="edit-actions">
                      <button className="save-btn" onClick={() => saveEdit(prop.id)}>
                        <Check size={16} />
                        Save
                      </button>
                      <button className="cancel-btn" onClick={() => setEditingId(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="proposition-content">
                      <span className="proposition-number">P{index + 1}</span>
                      <p>{prop.text.replace(/^P\d+:\s*/, '')}</p>
                    </div>
                    <div className="proposition-actions">
                      {prop.auto && <span className="auto-badge">Auto-generated</span>}
                      <button className="edit-btn" onClick={() => startEditing(prop)} title="Edit">
                        <Edit3 size={16} />
                      </button>
                      <button className="delete-btn" onClick={() => deleteProposition(prop.id)} title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>

        <div className="tip-box">
          <strong>Tip:</strong> Good propositions are specific, testable, and directly derived from your 
          theoretical relationships. Each proposition should be falsifiable through empirical research.
        </div>
      </div>

      <NavigationButtons
        onBack={previousStep}
        onContinue={handleContinue}
        canContinue={propositions.length >= 1}
        continueLabel="Continue to Validation"
      />
    </div>
  );
};

export default C5_GeneratePropositions;
